// ui-renderers-news.ts
/**
 * ui-renderers-news.js  —  Kili AI Assistant · News Segment Renderers
 *
 * Import into your existing ui-renderers.js or call from handlers-news.js.
 * All helpers follow the same escapeHtml / formatRelativeTime pattern used
 * elsewhere in the portal.
 */

/* ─── Utilities (use existing ones if already exported from ui-renderers.js) ── */

export function escapeHtml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Returns a human-readable relative time string.
 * @param {number} unixEpoch  — seconds since epoch
 * @returns {string}
 */
export function formatRelativeTime(unixEpoch) {
  if (!unixEpoch) return 'Unknown date';
  const diff    = Math.floor(Date.now() / 1000) - unixEpoch;
  const minutes = Math.floor(diff / 60);
  const hours   = Math.floor(diff / 3600);
  const days    = Math.floor(diff / 86400);

  if (diff < 60)       return 'Just now';
  if (minutes < 60)    return `${minutes}m ago`;
  if (hours   < 24)    return `${hours}h ago`;
  if (days    < 7)     return `${days}d ago`;
  if (days    < 30)    return `${Math.floor(days / 7)}w ago`;
  return new Date(unixEpoch * 1000).toLocaleDateString('en-KE', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

/* ─── Source badge metadata ─────────────────────────────────────────────────── */

const SOURCE_META = {
  // Kenyan news
  'The Standard':          { color: '#0077b6', abbr: 'STD',  icon: '📰' },
  'The Standard Business': { color: '#0096c7', abbr: 'STD-B',icon: '📈' },
  'Daily Nation':          { color: '#00509d', abbr: 'DN',   icon: '📰' },
  // Pan-African
  'AllAfrica Headlines':   { color: '#00875a', abbr: 'AA',   icon: '🌍' },
  'AllAfrica Business':    { color: '#00875a', abbr: 'AA-B', icon: '💹' },
  'BBC Africa':            { color: '#b71c1c', abbr: 'BBC',  icon: '📡' },
  'Africanews':            { color: '#e65100', abbr: 'AFN',  icon: '📡' },
  // Government / institutional
  'CBK Press Releases':    { color: '#c9a96e', abbr: 'CBK',  icon: '🏦' },
  'Parliament Hansard (Mzalendo)': { color: '#7b2d8b', abbr: 'PARL', icon: '🏛️' },
  'Kenya Gazette Archive': { color: '#4a148c', abbr: 'GAZ',  icon: '📋' },
  'African Union Press Releases': { color: '#006064', abbr: 'AU',  icon: '🌐' },
  '_default':              { color: '#37474f', abbr: '??',   icon: '🔗' },
};

const DOCTYPE_LABELS = {
  news:             { label: 'News',         color: 'var(--sq-cyan,#00e5ff)' },
  press_release:    { label: 'Press',        color: 'var(--sq-gold,#C9A96E)' },
  economic_report:  { label: 'Economic',     color: 'var(--sq-jade,#00CFA8)' },
  gazette:          { label: 'Gazette',      color: 'var(--sq-purple,#9B6DFF)' },
  hansard:          { label: 'Hansard',      color: 'var(--sq-purple,#9B6DFF)' },
  policy:           { label: 'Policy',       color: 'var(--sq-amber,#F59E0B)' },
  research_paper:   { label: 'Research',     color: 'var(--sq-amber,#F59E0B)' },
  other:            { label: 'Other',        color: 'rgba(136,153,187,.6)'   },
};

function getSourceMeta(sourceName) {
  return SOURCE_META[sourceName] ?? SOURCE_META['_default'];
}

function getDoctypeMeta(docType) {
  return DOCTYPE_LABELS[docType] ?? DOCTYPE_LABELS['other'];
}

/* ─── Recency classification ────────────────────────────────────────────────── */

/**
 * Returns CSS modifier class + aria-label hint for age.
 * @param {number} publishedAt  — unix seconds
 * @returns {{ cls: string, label: string }}
 */
function recencyInfo(publishedAt) {
  if (!publishedAt) return { cls: '', label: '' };
  const ageMs  = Date.now() - publishedAt * 1000;
  const ageHrs = ageMs / 3_600_000;
  const ageDays = ageHrs / 24;

  if (ageHrs < 2)    return { cls: 'breaking',  label: 'Breaking' };
  if (ageHrs < 8)    return { cls: 'fresh',     label: 'Fresh'    };
  if (ageDays < 1)   return { cls: 'today',     label: 'Today'    };
  if (ageDays < 3)   return { cls: 'recent',    label: ''         };
  return               { cls: '',           label: ''         };
}

/* ─── Card renderer ─────────────────────────────────────────────────────────── */

/**
 * Renders a single article card HTML string.
 * @param {import('./api-news.js').Article} article
 * @param {number} index — for staggered animation delay
 * @returns {string} HTML
 */
export function renderNewsCard(article, index = 0) {
  const src   = getSourceMeta(article.source_name);
  const dtype = getDoctypeMeta(article.doc_type);
  const rel   = recencyInfo(article.published_at);
  const delay = Math.min(index * 40, 320); // stagger cap at 320ms

  const summaryText = article.summary
    ? escapeHtml(article.summary.slice(0, 140)) + (article.summary.length > 140 ? '…' : '')
    : '<span class="ai-news-card-nosummary">No preview available.</span>';

  const breakingBadge = rel.cls === 'breaking'
    ? `<span class="ai-news-card-breaking" aria-label="Breaking news">
         <span class="ai-news-card-breaking-dot"></span>BREAKING
       </span>`
    : '';

  return `
<article class="ai-news-card ai-news-card--${rel.cls || 'normal'}"
         role="listitem"
         tabindex="0"
         data-id="${escapeHtml(article.id)}"
         data-url="${escapeHtml(article.source_url)}"
         data-title="${escapeHtml(article.title)}"
         style="animation-delay:${delay}ms">

  <div class="ai-news-card-top">
    <span class="ai-news-card-source-badge"
          style="--src-color:${src.color};">
      ${escapeHtml(src.icon)} ${escapeHtml(src.abbr)}
    </span>
    <span class="ai-news-card-doctype"
          style="color:${dtype.color};">
      ${escapeHtml(dtype.label)}
    </span>
    ${breakingBadge}
    <span class="ai-news-card-time" title="${article.published_at
      ? new Date(article.published_at * 1000).toLocaleString('en-KE')
      : 'Unknown'}">
      ${formatRelativeTime(article.published_at)}
    </span>
  </div>

  <h3 class="ai-news-card-title">${escapeHtml(article.title)}</h3>

  <p class="ai-news-card-summary">${summaryText}</p>

  <div class="ai-news-card-footer">
    <span class="ai-news-card-source-full">${escapeHtml(article.source_name ?? '')}</span>
    <div class="ai-news-card-actions">
      <button class="ai-news-card-ask"
              data-action="news-ask-kili"
              data-id="${escapeHtml(article.id)}"
              data-title="${escapeHtml(article.title)}"
              title="Ask Kili about this article"
              aria-label="Ask Kili about: ${escapeHtml(article.title)}">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
        </svg>
        Ask Kili
      </button>
      <a class="ai-news-card-open"
         href="${escapeHtml(article.source_url)}"
         target="_blank" rel="noopener noreferrer"
         title="Open original article"
         aria-label="Open article in new tab"
         data-action="news-open-external">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
          <polyline points="15 3 21 3 21 9"/>
          <line x1="10" y1="14" x2="21" y2="3"/>
        </svg>
      </a>
    </div>
  </div>
</article>`.trim();
}

/* ─── List renderer ─────────────────────────────────────────────────────────── */

/**
 * Renders the full articles list into #ai-news-list.
 * Handles loading / empty / error states on surrounding elements.
 *
 * @param {import('./api-news.js').Article[]} articles
 * @param {{ append?: boolean }} [opts]
 */
export function renderNewsList(articles, { append = false } = {}) {
  const list    = document.getElementById('ai-news-list');
  const loading = document.getElementById('ai-news-loading');
  const empty   = document.getElementById('ai-news-empty');
  const errEl   = document.getElementById('ai-news-error');
  const footer  = document.getElementById('ai-news-footer');

  if (!list) return;

  if (loading) loading.style.display = 'none';
  if (errEl)   errEl.style.display   = 'none';

  if (!articles.length && !append) {
    empty.style.display = '';
    list.innerHTML      = '';
    if (footer) footer.style.display = 'none';
    return;
  }

  empty.style.display = 'none';

  const html = articles.map((a, i) => renderNewsCard(a, append ? i + list.children.length : i));

  if (append) {
    list.insertAdjacentHTML('beforeend', html.join(''));
  } else {
    list.innerHTML = html.join('');
  }
}

/* ─── Count badge ───────────────────────────────────────────────────────────── */

/**
 * Updates the article count badge in the panel header.
 * @param {number} shown
 * @param {number} total
 */
export function renderNewsCount(shown, total) {
  const el = document.getElementById('ai-news-count');
  if (!el) return;
  if (total > 0) {
    el.textContent = shown < total ? `${shown} / ${total}` : `${total}`;
    el.style.display = '';
  } else {
    el.style.display = 'none';
  }
}

/* ─── Loading state ─────────────────────────────────────────────────────────── */

export function showNewsLoading(isLoading) {
  const loading = document.getElementById('ai-news-loading');
  const list    = document.getElementById('ai-news-list');
  if (!loading) return;
  loading.style.display = isLoading ? '' : 'none';
  if (isLoading && list) list.innerHTML = '';
  // FIX: also reset empty/error divs so they don't remain visible behind the
  // skeleton when the user refreshes after a failed or empty load.
  if (isLoading) {
    const empty = document.getElementById('ai-news-empty');
    const errEl = document.getElementById('ai-news-error');
    if (empty) empty.style.display = 'none';
    if (errEl) errEl.style.display = 'none';
  }
}

/* ─── Error state ───────────────────────────────────────────────────────────── */

export function showNewsError(message) {
  const errEl  = document.getElementById('ai-news-error');
  const errMsg = document.getElementById('ai-news-error-msg');
  const loading = document.getElementById('ai-news-loading');
  if (loading) loading.style.display = 'none';
  if (!errEl) return;
  errEl.style.display = '';
  if (errMsg) errMsg.textContent = message ?? 'An error occurred.';
}

/* ─── Inline preview renderer ───────────────────────────────────────────────── */

/**
 * Populates and slides in the inline article preview drawer.
 * @param {import('./api-news.js').Article} article
 */
export function renderNewsPreview(article) {
  const preview  = document.getElementById('ai-news-preview');
  const body     = document.getElementById('ai-news-preview-body');
  const extLink  = document.getElementById('ai-news-preview-external') as HTMLAnchorElement | null;
  if (!preview || !body) return;

  const src   = getSourceMeta(article.source_name);
  const dtype = getDoctypeMeta(article.doc_type);

  if (extLink) {
    extLink.href = article.source_url;
    extLink.title = `Open on ${article.source_name}`;
  }

  body.innerHTML = `
    <div class="ai-news-preview-meta">
      <span class="ai-news-preview-source" style="--src-color:${src.color};">
        ${escapeHtml(src.icon)} ${escapeHtml(article.source_name ?? '')}
      </span>
      <span class="ai-news-preview-dtype" style="color:${dtype.color};">
        ${escapeHtml(dtype.label)}
      </span>
      <span class="ai-news-preview-time">
        ${formatRelativeTime(article.published_at)}
      </span>
    </div>

    <h2 class="ai-news-preview-title">${escapeHtml(article.title)}</h2>

    <div class="ai-news-preview-content">
      ${article.content
        ? escapeHtml(article.content)
            .replace(/\n\n+/g, '</p><p>')
            .replace(/^/, '<p>')
            .replace(/$/, '</p>')
        : article.summary
          ? `<p class="ai-news-preview-summary">${escapeHtml(article.summary)}</p>
             <p class="ai-news-preview-hint">Full article available at source →</p>`
          : `<p class="ai-news-preview-hint">No preview available. Open the original article to read more.</p>`
      }
    </div>`;

  preview.classList.add('ai-news-preview--open');
}

/**
 * Collapses the inline preview drawer.
 */
export function closeNewsPreview() {
  const preview = document.getElementById('ai-news-preview');
  if (preview) preview.classList.remove('ai-news-preview--open');
}

/* ─── Breaking-news pulse dot ───────────────────────────────────────────────── */

export function setNewsPulseDot(hasBreaking) {
  const dot = document.getElementById('ai-news-pulse');
  const liveDot = document.getElementById('ai-news-live-dot');
  if (dot)     dot.style.display     = hasBreaking ? '' : 'none';
  if (liveDot) liveDot.style.display = '';
}

/* ─── Refresh spinner on button ─────────────────────────────────────────────── */

export function setNewsRefreshSpinning(spinning) {
  const btn = document.getElementById('ai-news-refresh-btn');
  if (!btn) return;
  if (spinning) btn.classList.add('ai-news-refresh-btn--spin');
  else          btn.classList.remove('ai-news-refresh-btn--spin');
}
